import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  Avatar,
  Pagination,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import Seo from '../components/Seo';
import { apiConfig } from '../config/api';

const API_BASE_URL = apiConfig.baseURL;
const IMAGE_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return `${IMAGE_BASE_URL}/uploads/default-blog.jpg`;
  if (imagePath.startsWith('http')) return imagePath;
  
  // If path already starts with /uploads/, use it as is
  if (imagePath.startsWith('/uploads/')) {
    return `${IMAGE_BASE_URL}${imagePath}`;
  }
  
  // If path doesn't start with /, add /uploads/blogs/
  if (!imagePath.startsWith('/')) {
    return `${IMAGE_BASE_URL}/uploads/blogs/${imagePath}`;
  }
  
  return `${IMAGE_BASE_URL}${imagePath}`;
};

const BlogsDynamic = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const postsPerPage = 30;

  useEffect(() => {
    fetchCategories();
    fetchFeaturedBlogs();
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [selectedCategory, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/Blog/categories`);
      const activeCategories = (response.data || []).filter(cat => cat.isActive);
      setCategories(activeCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFeaturedBlogs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/Blog/featured?count=3`);
      console.log('Featured blogs response:', response.data);
      console.log('First blog featuredImage:', response.data[0]?.featuredImage);
      console.log('First blog full object:', JSON.stringify(response.data[0]));
      setFeaturedBlogs(response.data || []);
    } catch (error) {
      console.error('Error fetching featured blogs:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        pageSize: postsPerPage,
        isPublished: true
      };
      
      if (selectedCategory !== 'all') {
        params.categoryId = selectedCategory;
      }

      const response = await axios.get(`${API_BASE_URL}/Blog`, { params });
      console.log('Blogs response:', response.data);
      setBlogs(response.data.data || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / postsPerPage);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Seo
        title="Hotel Booking Tips & Updates | Sintra Hotel Islamabad"
        description="Explore Sintra Hotel Islamabad's blog for hotel booking tips, city updates, news, and weather forecasts to enhance your stay and experience in Islamabad."
        keywords="Islamabad updates, Islamabad news, Islamabad weather news, hotel at Islamabad, hotel room Islamabad, Islamabad hotel rates, Islamabad news update"
        canonical="https://thesintrahotel.com/blog-hotel-islamabad-updates"

      />
      {/* Hero Section */}
           <Box
              sx={{
                position: 'relative',
                minHeight: { xs: '360px', md: '430px' },
                display: 'flex',
                alignItems: 'flex-end',
                backgroundImage: 'linear-gradient(rgba(0,0,0,0.28), rgba(0,0,0,0.38)), url(/img/home/hotel.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, pb: { xs: 6, md: 9 } }}>
                <Box sx={{ maxWidth: '520px', color: '#fff', textAlign: 'left' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      letterSpacing: '6px',
                      textTransform: 'uppercase',
                      mb: 1.5,
                      color: '#ffffff',
                    }}
                  >
                    Luxury Hotel
                  </Typography>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: 'Gilda Display, serif',
                      fontWeight: 400,
                      fontSize: { xs: '42px', md: '60px' },
                      lineHeight: 1.15,
                      color: '#ffffff',
                    }}
                  >
                    Blogs
                  </Typography>
                </Box>
              </Container>
            </Box>

      <Container maxWidth="xl" sx={{ py: 8 }}>
        {/* Category Filter */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight="700" sx={{ mb: 4, textAlign: 'center', color: '#111827' }}>
            Explore Our Stories
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Chip
              label={`All Posts (${totalCount})`}
              onClick={() => handleCategoryChange('all')}
              variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: 3,
                height: 'auto',
                backgroundColor: selectedCategory === 'all' ? '#2563eb' : 'transparent',
                color: selectedCategory === 'all' ? 'white' : '#374151',
                borderColor: selectedCategory === 'all' ? '#2563eb' : '#d1d5db',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: selectedCategory === 'all' ? '#1d4ed8' : '#f3f4f6',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
            />
            {categories.map((category) => (
              <Chip
                key={category.categoryID}
                label={`${category.categoryName} (${category.postCount || 0})`}
                onClick={() => handleCategoryChange(category.categoryID)}
                variant={selectedCategory === category.categoryID ? 'filled' : 'outlined'}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: 3,
                  height: 'auto',
                  backgroundColor: selectedCategory === category.categoryID ? '#2563eb' : 'transparent',
                  color: selectedCategory === category.categoryID ? 'white' : '#374151',
                  borderColor: selectedCategory === category.categoryID ? '#2563eb' : '#d1d5db',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: selectedCategory === category.categoryID ? '#1d4ed8' : '#f3f4f6',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Featured Posts */}
        {selectedCategory === 'all' && featuredBlogs.length > 0 && (
          <Box sx={{ mb: 8 }}>
            
             <section class="news">
             
        <div class="container">
           <Typography variant="h4" fontWeight="700" sx={{ mb: 4, color: '#111827' }}>
              Featured Stories
            </Typography>
            <div class="row">
              {featuredBlogs.map((post) => (
                  <div class="col-md-4 mb-30">
                    <div class="item">
                            <div class="position-re o-hidden"> <img src={post.featuredImage ? `${IMAGE_BASE_URL}${post.featuredImage}` : `${IMAGE_BASE_URL}/uploads/default-blog.jpg`} alt={post.title} />
                                <div class="date">
                                    <a href={post.slug}> <span>{new Date(post.publishedDate).toLocaleDateString("en-US", {
  month: "short",
}).toUpperCase()}</span> <i>{new Date(post.publishedDate).toLocaleDateString("en-US", {
day: "numeric",
})}</i> </a>
                                </div>
                            </div>
                            <div class="con"> <span class="category">
                                    <a href={`/blog/${post.slug}`}>{post.author}</a>
                                </span>
                                <h5><a href={`/blog/${post.slug}`}>{post.title}</a></h5>
                            </div>
                        </div>
                </div>
              ))}

              
            </div>
          </div>
        </section>
        </Box>
        )}

        {/* All Stories */}
        <section class="news">
        <div class="container">
          <Typography variant="h4" fontWeight="700" sx={{ mb: 4, color: '#111827' }}>
            All Stories
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : blogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No blog posts found
              </Typography>
            </Box>
          ) : (
           <div class="row">
              {blogs.map((post) => (
                <div class="col-md-4 mb-30">
                    <div class="item">
                            <div class="position-re o-hidden"> <img src={post.featuredImage ? `${IMAGE_BASE_URL}${post.featuredImage}` : `${IMAGE_BASE_URL}/uploads/default-blog.jpg`} alt={post.title} />
                                <div class="date">
                                    <a href={post.slug}> <span>{new Date(post.publishedDate).toLocaleDateString("en-US", {
  month: "short",
}).toUpperCase()}</span> <i>{new Date(post.publishedDate).toLocaleDateString("en-US", {
day: "numeric",
})}</i> </a>
                                </div>
                            </div>
                            <div class="con"> <span class="category">
                                    <a href={`/blog/${post.slug}`}>{post.author}</a>
                                </span>
                                <h5><a href={`/blog/${post.slug}`}>{post.title}</a></h5>
                            </div>
                        </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BlogsDynamic;
